import type {
  RiskAssessmentCodeAreaEnum,
  RiskAssessmentCodeResponse,
} from 'src/api/ProactApiClient';
import type {PillProps} from 'src/design/atoms/Pill';
import {RiskAssessmentCodeAreaPill} from 'src/shared/constants/PillConstants';
import type {ISelectOption} from 'src/types/Form';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';

export const FormErrorText = {
  fieldRequired: 'This field is required.',
  onlyNumberAllowed: 'Only numbers are allowed.',
  stringAndSpecialChar:
    'Only alphanumeric and standard special characters allowed.',
};

export const RiskAssessmentHeaderMeta = (
  areas: ISelectOption[]
): IFormFieldV2[] => [
  {
    name: 'name',
    label: 'Name',
    type: 'text',
    required: true,
    validation: {
      maxLength: 255,
    },
    builderOptions: {
      defaultValue: 'New Risk Assessment Code',
    },
  },
  {
    name: 'area',
    label: 'Risk Assessment Area',
    type: 'select',
    required: true,
    options: areas || [],
  },
];

export const RiskAssessmentLabelsMeta = (): IFormFieldV2[] => [
  {
    label: 'Probability Labels',
    name: 'probability_header',
    type: 'heading',
  },
  {
    name: 'probability_1',
    label: '1',
    type: 'text',
    required: true,
    validation: {
      maxLength: 255,
    },
    builderOptions: {
      defaultValue: 'Very Unlikely (has not happened before)',
    },
  },
  {
    name: 'probability_2',
    label: '2',
    type: 'text',
    required: true,
    validation: {
      maxLength: 255,
    },
    builderOptions: {
      defaultValue: 'Slight (rarely occurs)',
    },
  },
  {
    name: 'probability_3',
    label: '3',
    type: 'text',
    required: true,
    validation: {
      maxLength: 255,
    },
    builderOptions: {
      defaultValue: 'Possible (not common)',
    },
  },
  {
    name: 'probability_4',
    label: '4',
    type: 'text',
    required: true,
    validation: {
      maxLength: 255,
    },
    builderOptions: {
      defaultValue: 'Likely (has happened before, can happen again)',
    },
  },
  {
    name: 'probability_5',
    label: '5',
    type: 'text',
    required: true,
    validation: {
      maxLength: 255,
    },
    builderOptions: {
      defaultValue: 'Very Likely (occurs frequently)',
    },
  },
  {
    label: 'Severity Labels',
    name: 'severity_header',
    type: 'heading',
  },
  {
    name: 'severity_1',
    label: '1',
    type: 'text',
    required: true,
    validation: {
      maxLength: 255,
    },
    builderOptions: {
      defaultValue: 'Near Miss (no injury or property damage)',
    },
  },
  {
    name: 'severity_2',
    label: '2',
    type: 'text',
    required: true,
    validation: {
      maxLength: 255,
    },
    builderOptions: {
      defaultValue:
        'First Aid Incident (basic first aid, OTC medications) / Property Damage (no repairs needed)',
    },
  },
  {
    name: 'severity_3',
    label: '3',
    type: 'text',
    required: true,
    validation: {
      maxLength: 255,
    },
    builderOptions: {
      defaultValue:
        'Recordable Incident (advanced care required, i.e. stitches, prescriptions) / Partial Loss of property (work is not interrupted)',
    },
  },
  {
    name: 'severity_4',
    label: '4',
    type: 'text',
    required: true,
    validation: {
      maxLength: 255,
    },
    builderOptions: {
      defaultValue:
        'Lost Time Incident (days away from work) / Partial Loss of Property (work is interrupted)',
    },
  },
  {
    name: 'severity_5',
    label: '5',
    type: 'text',
    required: true,
    validation: {
      maxLength: 255,
    },
    builderOptions: {
      defaultValue: 'Fatality/Total Loss of Property',
    },
  },
];

export const MapRiskAssessmentResponseToFormData = (
  resp: RiskAssessmentCodeResponse
): IFormDataV2 => {
  return {
    ...resp,
    probability_1: resp.probability?.[1],
    probability_2: resp.probability?.[2],
    probability_3: resp.probability?.[3],
    probability_4: resp.probability?.[4],
    probability_5: resp.probability?.[5],
    severity_1: resp.severity?.[1],
    severity_2: resp.severity?.[2],
    severity_3: resp.severity?.[3],
    severity_4: resp.severity?.[4],
    severity_5: resp.severity?.[5],
  };
};

export const RiskAssessmentPillConfig = (
  area?: RiskAssessmentCodeAreaEnum
): PillProps[] => {
  if (area) {
    return [RiskAssessmentCodeAreaPill(area)];
  }

  return [];
};

export const RiskAssessmentCodeAreaPillConfig = (
  area: RiskAssessmentCodeAreaEnum
): {label: string; color: string} => {
  const pill = RiskAssessmentCodeAreaPill(area);
  return {label: pill.text, color: pill.color ?? 'gray'};
};

export const racDocumentTitle = 'Risk Assessment Code';
